import { useState, useEffect, useCallback, useRef } from 'react';
import { useSupabase } from '@/hooks/useSupabase';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('useEncompassFieldSync', 'hooks');

export interface PendingWrite {
  id: string;
  encompass_loan_id: string;
  field_id: string;
  field_value: string;
  db_column: string;
  // 'cancelled' added in Phase 2 Round 2B.9 — the pricing_cancel_write_group
  // RPC (2B.8) flips pending_encompass_writes rows to 'cancelled' when a
  // grouped save is cancelled pre-push. Worker claim filter IN ('pending',
  // 'failed') already excludes 'cancelled', so no other hook branch changes.
  status: 'pending' | 'syncing' | 'synced' | 'failed' | 'cancelled';
  attempts: number;
  last_error: string | null;
  created_at: string;
  updated_at: string;
}

export interface SyncStatus {
  status: 'synced' | 'pending' | 'failed' | null;
  error?: string;
}

export interface EnqueueWriteParams {
  fieldId: string;
  value: string;
  changeReason?: string;
}

export interface EnqueueResult {
  success: boolean;
  status: 'synced' | 'pending' | 'failed';
  error?: string;
  auditId?: string;
}

export interface EncompassFieldSync {
  enqueueWrite: (params: EnqueueWriteParams) => Promise<EnqueueResult>;
  getSyncStatus: (fieldId: string) => SyncStatus;
  pendingWrites: PendingWrite[];
  isLoading: boolean;
  refresh: () => Promise<void>;
}

/**
 * Hook for managing bidirectional Encompass field syncs.
 * Provides a durable, server-side queue for writes that survives page refreshes.
 *
 * @param loanId - UUID of the loan in encompass_loans table
 * @param encompassLoanId - Encompass GUID for the loan
 */
export function useEncompassFieldSync(
  loanId: string,
  encompassLoanId: string
): EncompassFieldSync {
  const supabase = useSupabase();
  const [pendingWrites, setPendingWrites] = useState<PendingWrite[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  // Generation guard: bumped on every effect run AND cleanup so a prior
  // loan's late fetch or realtime callback can never clobber the current
  // loan's state (stale async responses win otherwise).
  const genRef = useRef(0);

  /**
   * Fetch pending writes for this loan
   */
  const fetchPendingWrites = useCallback(async () => {
    if (!encompassLoanId) {
return;
}

    const gen = genRef.current;
    try {
      const { data, error } = await supabase
        .from('pending_encompass_writes')
        .select('*')
        .eq('encompass_loan_id', encompassLoanId)
        .order('updated_at', { ascending: false });

      if (genRef.current !== gen) {
        return; // stale response — a newer mount/loan owns the state now
      }
      if (error) {
        log.error('Failed to fetch pending writes', { encompassLoanId }, error);
        return;
      }

      setPendingWrites(data || []);
    } catch (err) {
      log.error('Exception fetching pending writes', { encompassLoanId }, err);
    } finally {
      if (genRef.current === gen) {
        setIsLoading(false);
      }
    }
  }, [supabase, encompassLoanId]);

  /**
   * Initial fetch and subscribe to realtime changes
   */
  useEffect(() => {
    genRef.current += 1;
    if (!encompassLoanId) {
      setPendingWrites([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setPendingWrites([]);
    fetchPendingWrites();

    // Subscribe to realtime changes for this loan's pending writes.
    // The topic must be UNIQUE PER MOUNT: supabase-js dedupes channels by
    // topic, so a second subscriber (LockDetailsCard + ConcessionsCureModal
    // both mount this hook in the pricing workspace, and StrictMode remounts
    // in dev) would be handed the already-subscribed instance — where .on()
    // throws on newer realtime-js. Distinct topics subscribe independently;
    // the resulting per-mount subscriptions (2–3 per loan view, multiplexed
    // over one websocket) are an accepted trade-off — no shared registry.
    // Setup is GUARDED: if it throws, live updates are disabled for this
    // mount and the hook degrades to fetch-only (enqueueWrite/getSyncStatus/
    // refresh unaffected) instead of crashing to the app error boundary.
    const gen = genRef.current;
    let channel: ReturnType<typeof supabase.channel> | null = null;

    const removeChannelSafely = (channelToRemove: ReturnType<typeof supabase.channel>) => {
      try {
        supabase.removeChannel(channelToRemove);
      } catch (err) {
        log.warn(
          'Failed to remove realtime channel',
          { metadata: { encompassLoanId } },
          err instanceof Error ? err : new Error(String(err))
        );
      }
    };

    try {
      channel = supabase.channel(`pending-writes-${encompassLoanId}-${Math.random().toString(36).slice(2)}`);

      channel.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'pending_encompass_writes',
          filter: `encompass_loan_id=eq.${encompassLoanId}`
        },
        (payload) => {
          if (genRef.current !== gen) {
            return; // late event for a prior mount/loan
          }
          log.info('Pending write changed', { type: payload.eventType, fieldId: (payload.new as PendingWrite)?.field_id });

          if (payload.eventType === 'INSERT') {
            setPendingWrites((prev) => [payload.new as PendingWrite, ...prev]);
          } else if (payload.eventType === 'UPDATE') {
            setPendingWrites((prev) =>
              prev.map((pw) => (pw.id === (payload.new as PendingWrite).id ? payload.new as PendingWrite : pw))
            );
          } else if (payload.eventType === 'DELETE') {
            setPendingWrites((prev) => prev.filter((pw) => pw.id !== (payload.old as PendingWrite).id));
          }
        }
      );

      channel.subscribe();
    } catch (err) {
      log.error(
        'Realtime subscription setup failed — live updates disabled for this mount',
        { metadata: { encompassLoanId } },
        err instanceof Error ? err : new Error(String(err))
      );
      if (channel) {
        removeChannelSafely(channel);
        channel = null;
      }
    }

    return () => {
      genRef.current += 1;
      if (channel) {
        removeChannelSafely(channel);
      }
    };
  }, [supabase, encompassLoanId, fetchPendingWrites]);

  /**
   * Enqueue a field write to Encompass
   */
  const enqueueWrite = useCallback(
    async (params: EnqueueWriteParams): Promise<EnqueueResult> => {
      const { fieldId, value, changeReason } = params;

      log.info('Enqueueing write', { loanId, fieldId, valueLength: value?.length });

      try {
        const { data, error } = await supabase.functions.invoke('enqueue-encompass-write', {
          body: {
            loanId,
            encompassLoanId,
            fieldId,
            value,
            ...(changeReason ? { changeReason } : {})
          }
        });

        if (error) {
          log.error('Enqueue function error', { loanId, fieldId }, error);
          return {
            success: false,
            status: 'failed',
            error: error.message || 'Failed to enqueue write'
          };
        }

        log.info('Enqueue result', { loanId, fieldId, status: data?.status });

        return {
          success: data?.success ?? false,
          status: data?.status ?? 'failed',
          error: data?.error,
          auditId: data?.auditId
        };
      } catch (err) {
        log.error('Enqueue exception', { loanId, fieldId }, err);
        return {
          success: false,
          status: 'failed',
          error: err instanceof Error ? err.message : 'Unknown error'
        };
      }
    },
    [supabase, loanId, encompassLoanId]
  );

  /**
   * Get sync status for a specific field
   */
  const getSyncStatus = useCallback(
    (fieldId: string): SyncStatus => {
      const pendingWrite = pendingWrites.find((pw) => pw.field_id === fieldId);

      if (!pendingWrite) {
        // No pending write = previously synced or never written
        return { status: null };
      }

      if (pendingWrite.status === 'synced') {
        return { status: 'synced' };
      }

      if (pendingWrite.status === 'failed') {
        return { status: 'failed', error: pendingWrite.last_error || 'Sync failed' };
      }

      if (pendingWrite.status === 'pending' || pendingWrite.status === 'syncing') {
        return { status: 'pending', error: pendingWrite.last_error || undefined };
      }

      // 'cancelled' is terminal-neutral — treat the field as "not currently
      // queued" so UI doesn't show a sync indicator. The pending write row is
      // kept for history; subsequent saves can still enqueue fresh writes.
      if (pendingWrite.status === 'cancelled') {
        return { status: null };
      }

      return { status: null };
    },
    [pendingWrites]
  );

  /**
   * Manual refresh of pending writes
   */
  const refresh = useCallback(async () => {
    if (!encompassLoanId) {
      setPendingWrites([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    await fetchPendingWrites();
  }, [encompassLoanId, fetchPendingWrites]);

  return {
    enqueueWrite,
    getSyncStatus,
    pendingWrites,
    isLoading,
    refresh
  };
}
